<template>
  <div v-if="mounted" class="admin-page">
    <h1>Datasets Management</h1>

    <div v-if="errors.general" class="form-error">{{ errors.general }}</div>

    <div class="filter-section">
      <div class="filter-buttons">
        <button
          :class="{ 'filter-active': showAll }"
          @click="toggleAll"
        >
          All
        </button>
        <button
          v-for="status in allStatuses"
          :key="status"
          :class="{ 'filter-active': selectedStatuses.includes(status) }"
          :disabled="showAll"
          @click="toggleStatusFilter(status)"
        >
          {{ status }}
        </button>
      </div>

      <div class="action-row">
        <button @click="refreshData" :disabled="loading" class="button-refresh">
          <span class="refresh-icon">↻</span> Refresh
        </button>
        <button @click="navigateToCreate">Create Dataset</button>
      </div>
    </div>

    <div v-if="loading" class="alert alert-info">
      Loading datasets...
    </div>

    <div v-else-if="filteredDatasets.length === 0" class="alert alert-info">
      No datasets found matching the selected filters.
    </div>

    <table v-else class="aws-table">
      <thead>
        <tr>
          <th>ID</th>
          <th>Description</th>
          <th>Status</th>
          <th>Email</th>
          <th>Store Set</th>
          <th>Group Set</th>
          <th>Dev</th>
          <th>Seed</th>
          <th>Error</th>
          <th>Date</th>
          <th>Duration</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="dataset in filteredDatasets"
          :key="dataset.id"
          :class="{ 'active-dataset': dataset.id === activeDatasetId }"
        >
          <td>
            {{ dataset.id }}
            <span v-if="dataset.id === activeDatasetId" class="active-badge">ACTIVE</span>
          </td>
          <td>{{ dataset.description || '-' }}</td>
          <td>
            <span :class="`status-badge status-${dataset.status}`">
              {{ dataset.status }}
            </span>
          </td>
          <td>{{ dataset.email || '-' }}</td>
          <td>{{ dataset.storeSet || '-' }}</td>
          <td>{{ dataset.groupSet || '-' }}</td>
          <td>{{ dataset.devMode ? 'Yes' : '-' }}</td>
          <td>{{ dataset.devMode ? dataset.devModeSeed ?? '-' : '-' }}</td>
          <td>{{ dataset.error || '-' }}</td>
          <td>{{ getDateForStatus(dataset) }}</td>
          <td>{{ formatDuration(dataset.durationMs) }}</td>
          <td>
            <div class="action-row">
              <button
                v-if="canActivate(dataset)"
                @click="handleActivate(dataset.id)"
              >
                Activate
              </button>
              <button
                v-if="canDelete(dataset)"
                @click="handleDelete(dataset)"
                :class="(dataset.status === 'launching' || dataset.status === 'processing' || dataset.status === 'deleted' || dataset.status === 'deleting') ? 'button-danger-processing' : ''"
              >
                {{ dataset.status === 'deleted' ? 'Remove' : 'Delete' }}
              </button>
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { ValueService } from "@/services";
import type { Dataset, DatsetStatus } from "@/data";

const router = useRouter();
const service = new ValueService();

const mounted = ref(false);
const loading = ref(false);
const datasets = ref<Dataset[]>([]);
const activeDatasetId = ref<string | undefined>(undefined);
const errors = ref<Record<string, string>>({});

const allStatuses: DatsetStatus[] = ["launching", "processing", "ready", "error", "deleting", "deleted"];
const selectedStatuses = ref<DatsetStatus[]>(["launching", "processing", "ready"]);
const savedStatuses = ref<DatsetStatus[]>([...selectedStatuses.value]);

const showAll = computed(() => allStatuses.every(s => selectedStatuses.value.includes(s)));

const filteredDatasets = computed(() => {
  return datasets.value.filter(ds => selectedStatuses.value.includes(ds.status));
});

function toggleAll() {
  if (showAll.value) {
    selectedStatuses.value = [...savedStatuses.value];
  } else {
    savedStatuses.value = [...selectedStatuses.value];
    selectedStatuses.value = [...allStatuses];
  }
}

function toggleStatusFilter(status: DatsetStatus) {
  const index = selectedStatuses.value.indexOf(status);
  if (index > -1) {
    selectedStatuses.value.splice(index, 1);
  } else {
    selectedStatuses.value.push(status);
  }
}

function formatDuration(durationMs?: number): string {
  if (!durationMs) {
    return '-';
  }

  const totalSeconds = Math.floor(durationMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const hh = String(hours).padStart(2, '0');
  const mm = String(minutes).padStart(2, '0');
  const ss = String(seconds).padStart(2, '0');

  return `${hh}:${mm}:${ss}`;
}

function getDateForStatus(dataset: Dataset): string {
  // Active dataset takes precedence - show when it was activated
  if (dataset.id === activeDatasetId.value && dataset.activatedAt) {
    return dataset.activatedAt;
  }

  // Otherwise show date based on status
  if (dataset.status === 'deleted' || dataset.status === 'deleting') {
    return dataset.deletedAt || '-';
  }

  if (dataset.status === 'processing') {
    return dataset.processedAt || '-';
  }

  if (dataset.status === 'ready' || dataset.status === 'error') {
    return dataset.completedAt || '-';
  }

  return '-';
}

function canActivate(dataset: Dataset): boolean {
  return dataset.status === "ready"
    && !dataset.groupSet
    && !dataset.storeSet
    && dataset.id !== activeDatasetId.value;
}

function canDelete(dataset: Dataset): boolean {
  // return (dataset.status === "ready" || dataset.status === "error" || dataset.status === "processing" || dataset.status === "deleted" || dataset.status === "deleting")
  //   && dataset.id !== activeDatasetId.value;
  return dataset.id !== activeDatasetId.value;
}

async function handleActivate(datasetId: string) {
  if (!confirm(`Are you sure you want to activate dataset "${datasetId}"? This will make it the active dataset for all users.`)) {
    return;
  }

  errors.value.general = "";
  loading.value = true;

  try {
    await service.activateDataset(datasetId);
    await loadData();
    setTimeout( () => {
      alert(`Dataset "${datasetId}" has been activated successfully.`);
    }, 0)
  } catch (error: any) {
    errors.value.general = error.message || "Failed to activate dataset";
    loading.value = false;
  }
}

async function handleDelete(dataset: Dataset) {
  let confirmMessage: string;

  if (dataset.status === "deleted") {
    confirmMessage = `Warning: This will permanently remove all record of dataset "${dataset.id}". This action cannot be undone. Are you sure you want to proceed?`;
  } else if (dataset.status === "deleting") {
    confirmMessage = `Dataset "${dataset.id}" deletion may still be in progress. The delete will be re-attempted. Are you sure you want to continue?`;
  } else if (dataset.status === "processing") {
    confirmMessage = `Dataset "${dataset.id}" may still be processing. Are you sure you want to delete it? This action cannot be undone.`;
  } else if (dataset.status === "launching") {
    confirmMessage = `Dataset "${dataset.id}" may still yet still process. Are you sure you want to delete it? This action cannot be undone.`;
  } else {
    confirmMessage = `Are you sure you want to delete dataset "${dataset.id}"? This action cannot be undone.`;
  }

  if (!confirm(confirmMessage)) {
    return;
  }

  errors.value.general = "";
  loading.value = true;

  try {
    await service.deleteDataset(dataset.id);
    await loadData();
    setTimeout( () => {
      alert(`Dataset "${dataset.id}" delete has been launched successfully.`);
    }, 0)
  } catch (error: any) {
    errors.value.general = error.message || "Failed to delete dataset";
    loading.value = false;
  }
}

function navigateToCreate() {
  router.push({ name: "dataset-create" });
}

async function refreshData() {
  await loadData();
}

async function loadData() {
  loading.value = true;
  errors.value.general = "";

  try {
    const [datasetsData, configData] = await Promise.all([
      service.fetchDatasets(),
      service.fetchAdminConfig()
    ]);

    datasets.value = datasetsData;
    activeDatasetId.value = configData?.activeDatasetId;
  } catch (error: any) {
    errors.value.general = error.message || "Failed to load datasets";
  } finally {
    loading.value = false;
  }
}

onMounted(async () => {
  await loadData();
  mounted.value = true;
});
</script>

<style scoped src="../admin.css"></style>

<style scoped>
.filter-section {
  margin-bottom: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  flex-wrap: wrap;
}

.filter-buttons {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.filter-buttons button {
  background-color: #e5e7eb;
  color: #374151;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
}

.filter-buttons button.filter-active {
  background-color: var(--color-primary-bg);
  color: var(--color-primary);
}

.filter-buttons button:hover:not(:disabled) {
  opacity: 0.8;
}

.filter-buttons button:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.active-dataset {
  background-color: #f0f9ff;
}

.active-badge {
  display: inline-block;
  margin-left: 0.5rem;
  padding: 0.125rem 0.5rem;
  background-color: #22c55e;
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
  border-radius: 0.25rem;
  text-transform: uppercase;
}

.status-badge {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
}

.status-launching {
  background-color: #dbeafe;
  color: #1e40af;
}

.status-processing {
  background-color: #fef3c7;
  color: #92400e;
}

.status-ready {
  background-color: #d1fae5;
  color: #065f46;
}

.status-error {
  background-color: #fee2e2;
  color: #991b1b;
}

.status-deleting {
  background-color: #f3e8ff;
  color: #6b21a8;
}

.status-deleted {
  background-color: #e5e7eb;
  color: #6b7280;
}

.button-danger {
  background-color: #dc2626;
}

.button-danger:hover {
  background-color: #b91c1c;
}

.button-danger-processing {
  background-color: #ef4444 !important;
  color: white !important;
  border: 2px solid #dc2626 !important;
}

.button-danger-processing:hover {
  background-color: #dc2626 !important;
  border: 2px solid #b91c1c !important;
}

.aws-table td {
  vertical-align: middle;
}

.button-refresh {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
}
</style>
